import React, { useEffect, useMemo, useState } from "react";
import * as R from "ramda";
import * as Dfns from "date-fns/fp";
import { Modal } from "react-bootstrap";
import "./MetaCreativeAssetModal.scss";
import {
  AdAssetPlacementGroup,
  MetaBuyingImage,
  MetaBuyingVideo,
} from "@blisspointmedia/bpm-types/dist/MetaBuying";
import { MdCheckCircle, MdCheckCircleOutline, MdCancel } from "react-icons/md";
import { metaAdFormDefault } from "../MetaBuyingContext";
import { DEFAULT_SELECTED_ASSETS, PLACEMENT_GROUP_OPTIONS } from "../MetaBuyingConstants";
import { getAssetValues, MissingCreativeWarning } from "../MetaBuyingUtils";
import { useStateFunction } from "../../utils/hooks/useData";
import { useSetError } from "../../redux/modals";
import { awaitJSON, MetaLambdaFetch } from "../../utils/fetch-utils";
import { MetaCreativeThumbnail } from "./MetaCreativeThumbnail";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  Dropdown,
  DropdownToggleType,
  FilterBar,
} from "../../Components";

interface MetaCreativeAssetModalProps {
  show: boolean;
  closeModal: any;
  defaultAssetFrame: AdAssetPlacementGroup;
  accountID: string;
  isVideo: boolean;
  adForm: typeof metaAdFormDefault;
  submitCreativeAssets: (assets: typeof DEFAULT_SELECTED_ASSETS) => void;
}

enum SortValues {
  ABC = "Abc",
  NEWEST = "Newest",
  OLDEST = "Oldest",
}

const sortByNameCaseInsensitive = R.sortBy(R.compose(R.toLower, R.prop("name")));
const sortByTitleCaseInsensitive = R.sortBy(R.compose(R.toLower, R.prop("title")));
const sortByNewestCreatedAt = R.sort(
  (a: MetaBuyingImage | MetaBuyingVideo, b: MetaBuyingImage | MetaBuyingVideo) =>
    Dfns.isAfter(Dfns.parseISO(a.created), Dfns.parseISO(b.created)) ? 1 : -1
);
const sortByOldestCreatedAt = R.sortBy(R.compose(Dfns.parseISO, R.prop("created")));
const sortAssets = (assets, sortVal: SortValues, isVideo) => {
  let sortedAssets = assets;
  try {
    switch (sortVal) {
      case "Abc":
        sortedAssets = isVideo
          ? sortByTitleCaseInsensitive(assets)
          : sortByNameCaseInsensitive(assets);
        break;
      case "Newest":
        sortedAssets = sortByNewestCreatedAt(assets);
        break;
      case "Oldest":
        sortedAssets = sortByOldestCreatedAt(assets);
        break;
    }
  } catch (e) {}
  return sortedAssets;
};

export const MetaCreativeAssetModal: React.FC<MetaCreativeAssetModalProps> = ({
  show,
  closeModal,
  defaultAssetFrame,
  accountID,
  isVideo = false,
  adForm,
  submitCreativeAssets,
}) => {
  const savedAssetSelections = useMemo(() => getAssetValues(adForm), [adForm]);

  const [sortVal, setSortVal] = useState<SortValues>(SortValues.NEWEST);
  const [loading, setLoading] = useState<boolean>(false);
  const [availableAssets, setAvailableAssets] = useState<
    MetaBuyingImage[] | MetaBuyingVideo[] | null
  >(null);
  const [selectedAssets, setSelectedAssets] = useState<typeof DEFAULT_SELECTED_ASSETS>(
    savedAssetSelections
  );
  const [filter, setFilter] = useStateFunction<(line) => boolean>(() => true);
  const [retryOnErrorCount, setRetryOnErrorCount] = useState<number>(0);
  const [assetPointer, setAssetPointer] = useState(0);
  const [assetThumbails, setAssetThumbails] = useState<JSX.Element[]>([]);
  const [assetFrame, setAssetFrame] = useState<typeof defaultAssetFrame>(defaultAssetFrame);
  const setError = useSetError();

  const creativeType = useMemo(() => (isVideo ? "video" : "image"), [isVideo]);

  const resetState = () => {
    setSortVal(SortValues.NEWEST);
    setLoading(false);
    setAvailableAssets([]);
    setSelectedAssets(savedAssetSelections);
    setRetryOnErrorCount(0);
    setAssetPointer(0);
    setAssetThumbails([]);
  };

  const filteredAssets = useMemo(() => {
    return R.filter(filter, availableAssets || []);
  }, [filter, availableAssets]);

  useEffect(() => {
    if (!loading && R.isNil(availableAssets) && retryOnErrorCount < 5) {
      setLoading(true);
      (async () => {
        try {
          const path = isVideo ? "/getMetaBuyingVideos" : "/getMetaBuyingImages";
          const res = await MetaLambdaFetch(path, {
            params: { accountIDs: accountID },
          });
          const assets = await awaitJSON(res);
          const sortedResults = sortAssets(assets[accountID] || [], SortValues.NEWEST, isVideo);
          if (R.isEmpty(sortedResults)) {
            setError({
              message: `No meta ${isVideo ? "video" : "image"}s found for accountId: ${accountID}`,
              onOkay: closeModal,
            });
          }
          setAvailableAssets(sortedResults);
          setAssetPointer(60);
        } catch (e) {
          const error = e as Error;
          setRetryOnErrorCount(c => {
            const currCount = c + 1;
            if (currCount > 4) {
              setError({
                message: `Error fetching meta assets for accountId: ${accountID}, Error: ${error.message}`,
              });
            }
            return currCount;
          });
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [setError, loading, availableAssets, accountID, retryOnErrorCount, isVideo, closeModal]);

  useEffect(() => {
    setAssetThumbails(
      filteredAssets.slice(0, assetPointer).map(el => (
        <MetaCreativeThumbnail
          key={`creativeAssetThumbnail${isVideo ? el.video_id : el.hash}`}
          isVideo={isVideo}
          selected={R.equals(selectedAssets[assetFrame], el)}
          url={isVideo ? el.picture : el.permalink_url}
          name={isVideo ? el.title : el.name}
          length={el.length}
          createdAt={el.created}
          onClick={() =>
            setSelectedAssets({
              ...selectedAssets,
              [assetFrame]: R.equals(el, selectedAssets[assetFrame]) ? undefined : el,
            })
          }
        />
      ))
    );
  }, [filteredAssets, isVideo, selectedAssets, assetPointer, assetFrame]);

  const FrameSelection = (): JSX.Element => (
    <div className="frameSelection">
      {PLACEMENT_GROUP_OPTIONS.map(({ value, text }) => (
        <Button
          key={`button-${value}`}
          className={assetFrame === value ? "selected" : ""}
          variant={ButtonFrameworkVariant.TRAILING_ICON}
          type={ButtonType.EMPTY}
          icon={selectedAssets[value] ? <MdCheckCircle /> : <MdCheckCircleOutline />}
          onClick={() => {
            setAssetFrame(value);
          }}
        >
          {text}
        </Button>
      ))}
    </div>
  );

  const CreativeSelectionPreview = ({ data }): JSX.Element => {
    const { text, value } = data;
    const asset = selectedAssets[value];

    return (
      <>
        <h5>{text}</h5>
        {asset ? (
          <div
            className="imagePreview"
            onClick={() =>
              setSelectedAssets({
                ...selectedAssets,
                [value]: undefined,
              })
            }
          >
            <img
              alt=""
              src={(asset as MetaBuyingImage).permalink_url || (asset as MetaBuyingVideo).picture}
            />
            <div className="removeSelectionIcon">
              <MdCancel size={20} color="#6b2def" />
            </div>
          </div>
        ) : (
          <MissingCreativeWarning creativeType={creativeType} selectedAssets={selectedAssets} />
        )}
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={() => closeModal()}
      className="MetaBuyingCreativeAssetModal"
      size={"xl"}
    >
      <Modal.Header closeButton>
        <Modal.Title>{isVideo ? "Choose Video" : "Choose Image"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="creativeAssetModalBody">
        <div className="filterOptions">
          <FilterBar
            options={isVideo ? ["title", "created"] : ["name", "created"]}
            onFilter={setFilter}
            size="lg"
          />
          <Dropdown
            type={DropdownToggleType.OUTLINED}
            label={"Sort by"}
            value={sortVal}
            onChange={option => {
              setSortVal(option);
              setAvailableAssets(a => sortAssets(a, option, isVideo));
            }}
            options={[SortValues.ABC, SortValues.NEWEST, SortValues.OLDEST]}
          />
        </div>
        <div className="assetSelectionContainer">
          <div className="thumbnailContainer">
            <FrameSelection />
            {assetThumbails}
            <div className="selectedAssetsContainer">
              <h4>Selected Creatives</h4>
              <div className="selectedAssets">
                {PLACEMENT_GROUP_OPTIONS.map(data => (
                  <CreativeSelectionPreview data={data} key={data.value} />
                ))}
              </div>
            </div>
            <div className="spacer" />
            {filteredAssets.length > assetPointer && (
              <div className="loadMoreButton">
                <Button
                  type={ButtonType.OUTLINED}
                  onClick={() => {
                    setAssetPointer(a => a + 60);
                  }}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type={ButtonType.OUTLINED}
          onClick={() => {
            resetState();
            closeModal();
          }}
        >
          Cancel
        </Button>
        <Button
          type={ButtonType.FILLED}
          disabled={R.all(asset => R.isNil(asset), R.values(selectedAssets))}
          onClick={() => {
            resetState();
            submitCreativeAssets(selectedAssets);
            closeModal();
          }}
        >
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
